"use client";

import Link from "next/link";
import { Fragment, useCallback, useEffect, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";
const EXPLORER_TX_URL = process.env.NEXT_PUBLIC_EXPLORER_TX_URL ?? "https://polygonscan.com/tx/";
const PAGE_SIZE = 25;

type LogEntry = {
  id: string;
  created_at: string;
  category: string;
  action: string;
  actor_email: string | null;
  actor_wallet: string | null;
  target_type: string | null;
  target_id: string | null;
  transaction_hash: string | null;
  details: Record<string, unknown> | null;
};

const SECTIONS: { key: string; label: string }[] = [
  { key: "", label: "All Activity" },
  { key: "CONTRACT", label: "Contracts" },
  { key: "DISTRIBUTOR", label: "Revenue Distributor" },
  { key: "PLATFORM_ACCOUNT", label: "Platform Accounts" },
  { key: "INVITATION", label: "Invitations" },
  { key: "SIGNUP", label: "Sign-ups" },
];

const CATEGORY_CLASS: Record<string, string> = {
  CONTRACT: "bg-blue-500/20 text-blue-300",
  DISTRIBUTOR: "bg-amber-500/20 text-amber-300",
  PLATFORM_ACCOUNT: "bg-purple-500/20 text-purple-300",
  INVITATION: "bg-emerald-500/20 text-emerald-300",
  SIGNUP: "bg-cyan-500/20 text-cyan-300",
};

export const dynamic = "force-dynamic";

function short(value: string | null, head = 8, tail = 6) {
  if (!value) return "—";
  return value.length > head + tail + 3 ? `${value.slice(0, head)}…${value.slice(-tail)}` : value;
}

export default function ActivityLogPage() {
  const { ready, authenticated, login, getAccessToken } = usePrivy();

  const [category, setCategory] = useState("");
  const [action, setAction] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [actions, setActions] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce the search box so every keystroke doesn't hit the API.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessToken();
      if (!token) throw new Error("Sign in again to view the activity log.");

      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (category) params.set("category", category);
      if (action) params.set("action", action);
      if (search) params.set("search", search);
      if (from) params.set("from", from);
      if (to) params.set("to", to);

      const response = await fetch(`${API_BASE_URL}/api/admin/audit-log?${params}`, {
        cache: "no-store",
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "Unable to load the activity log.");

      setEntries(payload.entries);
      setTotal(payload.total);
      setActions(payload.actions);
      setCounts(
        Object.fromEntries(payload.counts.map((c: { category: string; count: number }) => [c.category, c.count]))
      );
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load the activity log.");
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, page, category, action, search, from, to]);

  useEffect(() => {
    if (ready && authenticated) void load();
  }, [ready, authenticated, load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const allCount = Object.values(counts).reduce((sum, n) => sum + n, 0);

  function chooseSection(key: string) {
    setCategory(key);
    setAction("");
    setPage(1);
    setExpanded(null);
  }

  function clearFilters() {
    setAction("");
    setSearchInput("");
    setSearch("");
    setFrom("");
    setTo("");
    setPage(1);
  }

  if (!ready || !authenticated) {
    return (
      <main className="min-h-screen bg-slate-950 px-4 py-12 text-white">
        <button type="button" onClick={login} className="rounded-xl bg-blue-600 px-5 py-3 font-semibold">
          Sign in to view the activity log
        </button>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-10 text-white md:px-8">
      <div className="mx-auto max-w-7xl">
        <Link href="/login" className="text-sm text-blue-300">
          Back to my dashboard
        </Link>
        <h1 className="mt-3 text-4xl font-bold md:text-5xl">Activity Log</h1>
        <p className="mt-2 text-slate-300">
          Actions on the contracts and the platform, newest first. Only actions taken after logging was enabled are listed.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {SECTIONS.map((section) => {
            const count = section.key ? counts[section.key] ?? 0 : allCount;
            const active = category === section.key;
            return (
              <button
                key={section.key || "all"}
                type="button"
                onClick={() => chooseSection(section.key)}
                className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                  active
                    ? "border-blue-500 bg-blue-600 text-white"
                    : "border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500"
                }`}
              >
                {section.label} <span className="ml-1 text-xs opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-5">
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search email, wallet, agreement, tx hash, details"
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm md:col-span-2"
          />
          <select
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm"
          >
            <option value="">All action types</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {a.replaceAll("_", " ")}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            aria-label="From date"
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm"
          />
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            aria-label="To date"
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm"
          />
        </div>

        <div className="mt-3 flex items-center justify-between text-sm text-slate-400">
          <span>{loading ? "Loading…" : `${total.toLocaleString()} matching entries`}</span>
          <button type="button" onClick={clearFilters} className="text-blue-300 hover:text-blue-200">
            Clear filters
          </button>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-800 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Section</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Performed by</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Transaction</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                    No activity matches these filters.
                  </td>
                </tr>
              )}
              {entries.map((entry) => (
                <Fragment key={entry.id}>
                  <tr
                    onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                    className="cursor-pointer border-b border-slate-800/60 hover:bg-slate-800/40"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                      {new Date(entry.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          CATEGORY_CLASS[entry.category] ?? "bg-slate-700 text-slate-200"
                        }`}
                      >
                        {SECTIONS.find((s) => s.key === entry.category)?.label ?? entry.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">{entry.action.replaceAll("_", " ")}</td>
                    <td className="px-4 py-3 text-slate-300">
                      {entry.actor_email ?? short(entry.actor_wallet)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-300">
                      {entry.target_type ? `${entry.target_type}: ` : ""}
                      {short(entry.target_id)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {entry.transaction_hash ? (
                        <a
                          href={`${EXPLORER_TX_URL}${entry.transaction_hash}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-300 hover:text-blue-200"
                        >
                          {short(entry.transaction_hash)}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                  {expanded === entry.id && (
                    <tr className="border-b border-slate-800/60 bg-slate-950">
                      <td colSpan={6} className="px-4 py-3">
                        <pre className="overflow-x-auto whitespace-pre-wrap break-all text-xs text-slate-300">
                          {JSON.stringify(
                            {
                              actorWallet: entry.actor_wallet,
                              target: entry.target_id,
                              transactionHash: entry.transaction_hash,
                              details: entry.details,
                            },
                            null,
                            2
                          )}
                        </pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-slate-400">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </main>
  );
}
